<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Icon from '@/components/Icon.vue'
import Avatar from '@/components/Avatar.vue'
import { useUi } from '@/stores/ui'
import { useFloor } from '@/stores/floor'
import { useTransitions } from '@/stores/transitions'
import { fmtTime } from '@/lib/dates'
import { modeMeta } from '@/types/atrium'

const ui = useUi()
const floor = useFloor()
const tx = useTransitions()
const router = useRouter()

const pop = computed(() => ui.personPopover)
const person = computed(() => pop.value?.person ?? null)
const mode = computed(() => (person.value ? modeMeta(person.value.mode) : null))
const inRoom = computed(() =>
  person.value ? floor.rooms.find((r) => r.occupants.includes(person.value!.id)) ?? null : null,
)

const style = computed(() => {
  const a = pop.value?.anchor
  if (!a) return ''
  const w = 260
  let left = a.left + a.width / 2 - w / 2
  left = Math.max(12, Math.min(window.innerWidth - w - 12, left))
  const base = `position:fixed;left:${left}px;width:${w}px`
  // Open below the anchor; flip above when the bottom of the viewport is
  // closer than the popover is tall (and there's more room above).
  const spaceBelow = window.innerHeight - a.bottom
  if (spaceBelow < 320 && a.top > spaceBelow) {
    return `${base};bottom:${window.innerHeight - a.top + 10}px;max-height:${Math.max(120, a.top - 22)}px;overflow:auto`
  }
  return `${base};top:${a.bottom + 10}px;max-height:${Math.max(120, spaceBelow - 22)}px;overflow:auto`
})

const following = computed(() => !!person.value && tx.followingId === person.value.id)

function knock() { if (person.value) ui.openKnock(person.value) }
function grab() { if (person.value) { tx.grab(person.value); ui.closePerson() } }
function follow() { if (person.value) { tx.follow(person.value); ui.closePerson() } }
function join() { if (inRoom.value) { ui.closePerson(); void router.push(`/room/${inRoom.value.id}`) } }
</script>

<template>
  <div v-if="person && mode" class="modal-back is-popover in" @mousedown.self="ui.closePerson()">
    <div class="modal modal--pop" role="dialog" :style="style">
      <div class="pop-person">
        <div class="pop-person__top">
          <Avatar :person="person" :size="52" :tip="false" />
          <div>
            <div class="pop-person__n">{{ person.name }}</div>
            <div class="pop-person__mode" :style="`color:${mode.color}`">
              <Icon :name="mode.icon" :size="14" /> {{ mode.label }}
            </div>
          </div>
        </div>
        <div v-if="person.now" class="pop-person__now"><Icon name="note" :size="14" /> {{ person.now }}</div>
        <div v-if="person.meeting" class="pop-person__where" :style="`color:var(--p-meeting)`">
          <Icon name="cal2" :size="14" />
          in {{ person.meeting.summary ? `“${person.meeting.summary}”` : 'a meeting' }}<template v-if="person.meeting.end"> until {{ fmtTime(person.meeting.end) }}</template>
        </div>
        <div v-if="inRoom" class="pop-person__where"><Icon name="door" :size="14" /> in <b>{{ inRoom.name }}</b></div>
        <div class="pop-actions">
          <button class="pop-act" @click="knock"><Icon name="knock" :size="16" /> {{ person.mode === 'deep' || person.mode === 'break' ? 'Knock (queues)' : 'Knock' }}</button>
          <button class="pop-act" @click="grab"><Icon name="grab" :size="16" /> Grab into your room</button>
          <button v-if="inRoom" class="pop-act" @click="join"><Icon name="door" :size="16" /> Join {{ inRoom.name }}</button>
          <button class="pop-act" @click="follow">
            <Icon name="arrowR" :size="16" /> {{ following ? 'Stop following' : 'Follow them' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
